import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { debounceTime, merge, of } from "rxjs";
import { color as d3Color } from "d3";
import eventEmitter from "../../eventEmitter";
import pieDiagram from "./pieDiagram";
import Btn from "../Btn";

function getPercent(val, total) {
  const pc = Math.round((100 * val) / total) / 100;
  const labelConf = { maximumFractionDigits: 0, style: "percent" };
  return pc.toLocaleString(undefined, labelConf);
}

function processItems(items, filteredLabels) {
  const total = items.reduce((a, x) => a + x.value, 0);
  return items
    .map((x) => ({ ...x, isVisible: !filteredLabels[x.key] }))
    .map((x) => ({ ...x, value: x.isVisible ? x.value : 0 }))
    .map((x, i) => {
      const prevVal = i > 0 ? items[i - 1].value : 0;
      const opacity = 1 - i / items.length;
      const rgb = d3Color(x.color);
      const color = rgb.copy({ opacity }).formatHex8();
      const label = getPercent(x.value, total, prevVal);
      return { ...x, color, label, total };
    });
}

function PieChart({ title, data }) {
  const containerRef = useRef(null);
  const chart = useRef(null);
  const svgRef = useRef(null);
  const [filteredLabels, setFilteredLabels] = useState({});

  useEffect(() => {
    if (!chart.current) {
      chart.current = pieDiagram({ element: svgRef.current });
    }

    const sub = merge(of(null), eventEmitter.resizeTracker$)
      .pipe(debounceTime(100))
      .subscribe(() => {
        const { width, height } = containerRef.current.getBoundingClientRect();
        chart.current.setDimensions(width, height);
      });

    return () => sub.unsubscribe();
  }, [data]);

  const items = useMemo(() => {
    return processItems(data, filteredLabels);
  }, [data, filteredLabels]);

  useEffect(() => {
    if (!chart.current) return;
    chart.current.setData(items);
  }, [items]);

  useEffect(() => {
    return () => {
      if (chart.current) chart.current = null;
    };
  }, []);

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-items-stretch">
      <div className="tw-text-center tw-font-bold tw-opacity-80 tw-text-xs">
        {title}
      </div>
      <div className="tw-flex tw-flex-grow tw-items-center tw-flex-shrink">
        <div
          className="tw-flex-grow-333 tw-relative tw-h-36"
          ref={containerRef}
        >
          <div className="tw-absolute tw-inset-0">
            <svg ref={svgRef} className="tw-h-auto tw-overflow-visible " />
          </div>
        </div>

        <div className="tw-flex-grow tw-text-2xs tw-flex-shrink-0 tw-max-w-1/2 tw-p-2 tw-opacity-70">
          {items.map((x) => {
            const isOut = filteredLabels[x.key];
            const cl = isOut ? "tw-line-through" : "";
            const handleClick = () => {
              setFilteredLabels(() => ({
                ...filteredLabels,
                [x.key]: !filteredLabels[x.key],
              }));
            };

            return (
              <div key={x.key}>
                <Btn
                  onSubmit={handleClick}
                  className={`tw-text-left tw-flex tw-font-semibold ${cl}`}
                  data-tooltip={`${x.key}: ${x.label} (${x.value} of ${x.total})`}
                  data-tooltip-follow-mouse="true"
                  data-tooltip-class="plot-tooltip"
                >
                  <div
                    style={{ backgroundColor: x.color }}
                    className="tw-w-2 tw-h-2 tw-rounded-full tw-my-1 tw-flex-shrink-0"
                  />
                  <div className="tw-w-1" />

                  <div className="tw-break-normal">
                    {x.key.substring(0, 100)}
                  </div>
                </Btn>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

PieChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string.isRequired,
};

PieChart.defaultProps = { data: [] };

export default PieChart;
